// What has to happen: the race for the playoffs in words, in the regular
// season's last week (scenario H1, PLAN-2026-10-19-what-has-to-happen, B-460).
//
// EVERY COMBINATION OF THE LAST WEEK'S RESULTS, tried: `2^g` for `g` games,
// `3^g` where a game can end level, and in a median league each team's place
// against the median besides (PLAN-2026-10-19 §6). Each combination's final
// records say who is in, who is out, and who is level at the line — for the
// tiebreakers, which are not run forward, as the clinched and out marks do
// not (`playoffStatuses`): a points tiebreak depends on scores nobody has
// played. A league seeded division winners first is seeded so.
//
// SUFFICIENT, NEVER "ONLY": each part of a sentence is a route that is enough,
// and there may be others, which is why it reads *in with*, never *needs*.
//
// STORED AS WHO AND BY WHAT (`standings.race_note`), worded at the read with
// the teams' names as they are now, as a tie's note is (B-457).

import { z } from 'zod';

/** A team's game in the last week, by who plays at home and away. */
export interface RaceGame {
  home: string;
  away: string;
}

/** A team's record before the last week. */
export interface RaceTeam {
  teamId: string;
  wins: number;
  losses: number;
  ties: number;
}

/** What one of a team's results leads to, across everything else that can happen. */
export const RaceOutcome = z.discriminatedUnion('kind', [
  /** In, whatever else happens. */
  z.object({ kind: z.literal('in') }),
  /** Out, whatever else happens. */
  z.object({ kind: z.literal('out') }),
  /** In if any one of these teams loses. */
  z.object({ kind: z.literal('any-loses'), teams: z.array(z.string()).min(1) }),
  /** In if both of these lose. */
  z.object({ kind: z.literal('both-lose'), teams: z.tuple([z.string(), z.string()]) }),
  /** In with other help than one or two losses. */
  z.object({ kind: z.literal('help') }),
  /** Level at the line at best, for the tiebreakers. */
  z.object({ kind: z.literal('level') }),
]);
export type RaceOutcome = z.infer<typeof RaceOutcome>;

/** `standings.race_note`: a team's race in the last week, its own result first. */
export const RaceNote = z.discriminatedUnion('played', [
  z.object({
    played: z.literal(true),
    /** Its win; in a median week, both its wins. */
    win: RaceOutcome,
    /** In a median week only: one win and one loss, its game or the median. Added 2026-10-20. */
    split: RaceOutcome.optional(),
    /**
     * A week of two games (Q6): `win`, `split` and `loss` are its two games'
     * results, rather than its game's and the median's. Added 2026-11-01.
     */
    twoGames: z.literal(true).optional(),
    /** Its loss; in a median week, both its losses. */
    loss: RaceOutcome,
    /** Some results leave it level on record at the line. */
    tiebreak: z.boolean(),
  }),
  z.object({ played: z.literal(false), bye: RaceOutcome, tiebreak: z.boolean() }),
]);
export type RaceNote = z.infer<typeof RaceNote>;

/**
 * The most combinations searched: every ordinary week of a league of twenty
 * (`3^10` where a game can end level), and a median week of a league of
 * twelve (287,476 where a game can end level, 76,224 where it cannot). A
 * larger median league is not searched, and its marks still are.
 */
const MOST_COMBINATIONS = 400_000;
/** Past this many combinations, routes through two others' losses are not searched. */
const PAIRS_UP_TO = 16384;

/** In each combination, a team in, out, or level at the line. */
const IN = 0;
const OUT = 1;
const LEVEL = 2;

/** What the search found: each alive team's routes, and who is in or out whatever happens. */
export interface RaceSearch {
  notes: Map<string, RaceNote>;
  /**
   * Teams the search found in, or out, whatever the week's results — which
   * the clinched and out marks, which do not run the last week through, may
   * not have said yet. The caller's mark follows this.
   */
  settled: Map<string, 'clinched' | 'eliminated'>;
}

/** How the league's last week is played, and how its field is seeded. */
export interface RaceRules {
  /** A game can end level: the league's matchup rule is `tie`. */
  ties?: boolean;
  /** Every team also plays the league median this week (`playsTheMedian`). */
  median?: boolean;
  /**
   * Each team's division, where the league seeds division winners first
   * (`seedPlayoffField`). Absent, or every team without one, is the table.
   */
  divisions?: ReadonlyMap<string, string | null>;
}

/**
 * The week's combinations: for each, every game's result (0 the home side
 * wins, 1 the away side, 2 level) and, in a median league, every team's
 * place against the median (0 below, 1 above, 2 on it).
 */
interface Combinations {
  count: number;
  game: (combo: number, i: number) => number;
  median: ((combo: number, team: number) => number) | null;
}

/** Every combination of an ordinary week: `results^g`, each game independent. */
function ordinaryWeek(games: number, ties: boolean): Combinations | null {
  const results = ties ? 3 : 2;
  const count = results ** games;
  if (count > MOST_COMBINATIONS) return null;
  return {
    count,
    game: (combo, i) => Math.floor(combo / results ** i) % results,
    median: null,
  };
}

const BELOW = 0;
const ABOVE = 1;
const ON = 2;

/** The most teams on the median score at once that the search takes (PLAN-2026-10-19 §6). */
const MOST_ON = 2;

/**
 * Every combination of a median week (PLAN-2026-10-19 §6). A team scores
 * above the median, below it, or on it, a tie (`weekMedian`): with no score
 * repeated, half the league is above it and, in a league of an odd size, one
 * team on it; two teams level at the middle are both on it, and the rest
 * split as the count allows. A game's winner outscored its loser, so its
 * place is never below the loser's and the two are never both on the
 * median; a level game puts both on the same side. Three or more teams on
 * the very same median score is not searched (`MOST_ON`).
 */
function medianWeek(
  teamCount: number,
  games: readonly { home: number; away: number }[],
  byes: readonly number[],
  ties: boolean,
): Combinations | null {
  const odd = teamCount % 2 === 1;
  /** Where a place stands: below the median, on it, above it. */
  const height = (p: number) => (p === BELOW ? 0 : p === ON ? 1 : 2);
  // A GAME'S STATES: its result, and the home and away sides' places.
  const places3 = [BELOW, ON, ABOVE];
  const states: [number, number, number][] = [];
  for (const w of places3)
    for (const l of places3)
      if (height(w) >= height(l) && !(w === ON && l === ON)) states.push([0, w, l], [1, l, w]);
  if (ties) for (const p of places3) states.push([2, p, p]);
  const byeStates = places3;
  const half = Math.floor(teamCount / 2);
  /** Whether a week ends with `a` teams above the median and `o` on it. */
  const valid = (a: number, o: number) => {
    if (o > MOST_ON) return false;
    if (!odd) return (o === 0 && a === half) || (o === 2 && a === half - 1);
    const below = teamCount - a - o;
    return o >= 1 && a <= half && below <= half;
  };
  const slots = games.length + byes.length;
  const choicesAt = (k: number) => (k < games.length ? states.length : byeStates.length);
  const places = (k: number, c: number): number[] =>
    k < games.length ? [states[c]![1], states[c]![2]] : [byeStates[c]!];
  const aboveOf = (k: number, c: number) => places(k, c).filter((p) => p === ABOVE).length;
  const onOf = (k: number, c: number) => places(k, c).filter((p) => p === ON).length;

  // COUNTED FIRST, by how many are above and on the median after each slot.
  let ways = new Map<string, number>([['0,0', 1]]);
  for (let k = 0; k < slots; k += 1) {
    const next = new Map<string, number>();
    for (const [key, n] of ways) {
      const [a, o] = key.split(',').map(Number) as [number, number];
      for (let c = 0; c < choicesAt(k); c += 1) {
        const a2 = a + aboveOf(k, c);
        const o2 = o + onOf(k, c);
        if (a2 > half || o2 > MOST_ON) continue;
        next.set(`${a2},${o2}`, (next.get(`${a2},${o2}`) ?? 0) + n);
      }
    }
    ways = next;
  }
  let count = 0;
  for (const [key, n] of ways) {
    const [a, o] = key.split(',').map(Number) as [number, number];
    if (valid(a, o)) count += n;
  }
  if (count === 0 || count > MOST_COMBINATIONS) return null;

  // THEN WRITTEN, a state for each game and each bye, combination by combination.
  const picks = new Uint8Array(count * slots);
  const pick = new Uint8Array(slots);
  let written = 0;
  const walk = (k: number, a: number, o: number) => {
    if (a > half || o > MOST_ON) return;
    if (k === slots) {
      if (valid(a, o)) {
        picks.set(pick, written * slots);
        written += 1;
      }
      return;
    }
    // Too few places left to put as many above it as any week has.
    const left = 2 * Math.max(0, games.length - k) + Math.min(byes.length, slots - k);
    if (a + left < half - 1) return;
    for (let c = 0; c < choicesAt(k); c += 1) {
      pick[k] = c;
      walk(k + 1, a + aboveOf(k, c), o + onOf(k, c));
    }
  };
  walk(0, 0, 0);

  // WHERE EACH TEAM'S PLACE IS READ: its game's slot and side, or its bye's.
  const slotOf = new Int16Array(teamCount).fill(-1);
  const sideOf = new Int8Array(teamCount);
  games.forEach((g, i) => {
    slotOf[g.home] = i;
    sideOf[g.home] = 1;
    slotOf[g.away] = i;
    sideOf[g.away] = 2;
  });
  byes.forEach((team, j) => {
    slotOf[team] = games.length + j;
    sideOf[team] = 0;
  });
  return {
    count,
    game: (combo, i) => states[picks[combo * slots + i]!]![0],
    median: (combo, team) => {
      const c = picks[combo * slots + slotOf[team]!]!;
      const side = sideOf[team]!;
      return side === 0 ? byeStates[c]! : states[c]![side]!;
    },
  };
}

/**
 * The race in the last week's `games` with `seats` in the playoffs. Nothing
 * to search — no seats, a seat for everybody, no games, more combinations
 * than the search takes — is an empty search. Where a game can end level
 * (`rules.ties`), a route is said only if it holds whatever the other games'
 * results. A median week (`rules.median`) is two results a team, and a team's
 * own week splits three ways, two wins, one and none; a league seeded
 * division winners first (`rules.divisions`) is seeded so in every
 * combination (PLAN-2026-10-19 §6).
 */
export function raceNotes(
  teams: readonly RaceTeam[],
  games: readonly RaceGame[],
  seats: number,
  rules: RaceRules = {},
): RaceSearch {
  const found: RaceSearch = { notes: new Map(), settled: new Map() };
  if (seats <= 0 || seats >= teams.length || games.length === 0) return found;
  const index = new Map(teams.map((t, i) => [t.teamId, i]));
  if (games.some((g) => !index.has(g.home) || !index.has(g.away))) return found;
  const ties = rules.ties ?? false;
  const at = games.map((g) => ({ home: index.get(g.home)!, away: index.get(g.away)! }));
  const playing = new Set(at.flatMap((g) => [g.home, g.away]));
  // TODO(Q6): a week of two games and the median is three results of a
  // team's own, which neither the median's search nor a note takes yet; the
  // marks settle such a week without the search.
  if (rules.median && playing.size < at.length * 2) return found;
  const byes = teams.map((_, i) => i).filter((i) => !playing.has(i));
  const week = rules.median
    ? medianWeek(teams.length, at, byes, ties)
    : ordinaryWeek(games.length, ties);
  if (week === null) return found;
  const { count: combos, game: resultOf, median } = week;
  const loserOf = (combo: number, i: number): string | null => {
    const r = resultOf(combo, i);
    return r === 0 ? games[i]!.away : r === 1 ? games[i]!.home : null;
  };

  // THE SEEDING, by division where the league seeds so: each team's
  // division as a number, or -1 for none, which never wins one.
  const divisionIds = new Map<string, number>();
  const division = teams.map((t) => {
    const d = rules.divisions?.get(t.teamId) ?? null;
    if (d === null) return -1;
    if (!divisionIds.has(d)) divisionIds.set(d, divisionIds.size);
    return divisionIds.get(d)!;
  });
  const divided = divisionIds.size > 0;
  // Which divisions have a winner so far, by the pass that stamped them.
  const seenAt = new Int32Array(divisionIds.size);
  let stamp = 0;
  const winner = (o: number) => {
    const d = division[o]!;
    if (d < 0 || seenAt[d] === stamp) return false;
    seenAt[d] = stamp;
    return true;
  };
  /**
   * A team's place in the field, 0 the top seed, in a table ordered by
   * share with it `first` or last among the teams level with it: division
   * winners first where the league seeds so, then everybody else.
   */
  const seedOf = (
    order: readonly number[],
    share: readonly number[],
    t: number,
    first: boolean,
  ) => {
    // AHEAD OF IT IN THE TABLE: the teams above it, and those level with it
    // unless it is placed first among them.
    const mine = share[t]!;
    let ahead = 0;
    for (let o = 0; o < share.length; o += 1)
      if (o !== t && (share[o]! > mine || (!first && share[o] === mine))) ahead += 1;
    if (!divided) return ahead;
    // IN THE FIELD: down the table to its place, each division's first team
    // its winner, seeded above everybody who is not one.
    stamp += 1;
    let passed = 0;
    let winnersAhead = 0;
    let othersAhead = 0;
    for (const o of order) {
      if (o === t) continue;
      if (passed === ahead) break;
      if (winner(o)) winnersAhead += 1;
      else othersAhead += 1;
      passed += 1;
    }
    return winner(t) ? winnersAhead : divisionIds.size + othersAhead;
  };

  const n = teams.length;
  const standing = new Uint8Array(combos * n);
  const won = new Array<number>(n).fill(0);
  const played = new Array<number>(n).fill(0);
  const share = new Array<number>(n).fill(0);
  const ids = teams.map((_, i) => i);
  for (let combo = 0; combo < combos; combo += 1) {
    teams.forEach((t, i) => {
      won[i] = t.wins + t.ties / 2;
      played[i] = t.wins + t.losses + t.ties;
    });
    at.forEach((g, i) => {
      const r = resultOf(combo, i);
      played[g.home]! += 1;
      played[g.away]! += 1;
      if (r === 0) won[g.home]! += 1;
      else if (r === 1) won[g.away]! += 1;
      else {
        won[g.home]! += 0.5;
        won[g.away]! += 0.5;
      }
    });
    if (median !== null)
      for (let i = 0; i < n; i += 1) {
        const m = median(combo, i);
        played[i]! += 1;
        won[i]! += m === ABOVE ? 1 : m === ON ? 0.5 : 0;
      }
    for (let i = 0; i < n; i += 1) share[i] = played[i] === 0 ? 0 : won[i]! / played[i]!;
    const order = divided ? [...ids].sort((a, b) => share[b]! - share[a]! || a - b) : ids;
    for (let i = 0; i < n; i += 1) {
      // IN placed last among the teams level with it, the tiebreakers not
      // run forward; OUT placed first; LEVEL between.
      const worst = seedOf(order, share, i, false);
      const best = worst < seats ? worst : seedOf(order, share, i, true);
      standing[combo * n + i] = worst < seats ? IN : best >= seats ? OUT : LEVEL;
    }
  }
  const standingOf = (combo: number, t: number) => standing[combo * n + t]!;

  const all = Array.from({ length: combos }, (_, c) => c);
  teams.forEach((team, t) => {
    const mine = all.map((c) => standingOf(c, t));
    // IN OR OUT WHATEVER HAPPENS: the mark's, said by the caller.
    if (mine.every((s) => s === IN)) {
      found.settled.set(team.teamId, 'clinched');
      return;
    }
    if (mine.every((s) => s === OUT)) {
      found.settled.set(team.teamId, 'eliminated');
      return;
    }
    // ITS OWN GAMES: none on a bye, one, or two in a week of two (Q6).
    const owns = games.flatMap((g, i) =>
      g.home === team.teamId || g.away === team.teamId ? [i] : [],
    );
    const own = owns[0] ?? -1;
    const others = games.map((_, i) => i).filter((i) => !owns.includes(i));
    /**
     * A loss by `x` in a game other than this team's own: in a week of one
     * game a team's only one, in a week of two either of them.
     */
    const lostBy = (c: number, x: string) => others.some((i) => loserOf(c, i) === x);

    /** What a set of combinations leads to for this team. */
    const outcomeOf = (set: readonly number[]): RaceOutcome => {
      const here = set.map((c) => standingOf(c, t));
      if (here.every((s) => s === IN)) return { kind: 'in' };
      if (!here.includes(IN)) return here.includes(LEVEL) ? { kind: 'level' } : { kind: 'out' };
      const inWhen = (pick: (c: number) => boolean) => {
        const subset = set.filter(pick);
        return subset.length > 0 && subset.every((c) => standingOf(c, t) === IN);
      };
      // ONE OTHER TEAM LOSING its game, each such team listed: any one is enough.
      const singles: string[] = [];
      for (const i of others)
        for (const side of [games[i]!.home, games[i]!.away])
          if (!singles.includes(side) && inWhen((c) => lostBy(c, side))) singles.push(side);
      if (singles.length > 0) return { kind: 'any-loses', teams: singles };
      // TWO OTHERS BOTH LOSING: the first such pair, in the games' order.
      if (combos <= PAIRS_UP_TO)
        for (const [k, i] of others.entries())
          for (const j of others.slice(k + 1))
            for (const x of [games[i]!.home, games[i]!.away])
              for (const y of [games[j]!.home, games[j]!.away])
                if (x !== y && inWhen((c) => lostBy(c, x) && lostBy(c, y)))
                  return { kind: 'both-lose', teams: [x, y] };
      return { kind: 'help' };
    };

    const tiebreak = mine.includes(LEVEL) && mine.includes(IN);
    /** The note, unless one of its own results cannot happen (a league of three, say): then none. */
    const say = (note: RaceNote, sets: readonly (readonly number[])[]) => {
      if (sets.every((s) => s.length > 0)) found.notes.set(team.teamId, note);
    };
    // A MEDIAN WEEK: a team's place against the median is a result of its
    // own, and with its week off the only one.
    const placeOf = (c: number) => median!(c, t);
    if (own === -1) {
      if (median === null) {
        say({ played: false, bye: outcomeOf(all), tiebreak }, [all]);
        return;
      }
      const above = all.filter((c) => placeOf(c) === ABOVE);
      const below = all.filter((c) => placeOf(c) === BELOW);
      say({ played: true, win: outcomeOf(above), loss: outcomeOf(below), tiebreak }, [
        above,
        below,
      ]);
      return;
    }
    // ITS OWN WIN AND LOSS; a level game of its own, or a score on the
    // median, is a result the line does not name, and the routes for a win
    // or a loss hold whatever the other games' results, a level one included.
    const wonIn = (c: number, i: number) =>
      resultOf(c, i) === (games[i]!.home === team.teamId ? 0 : 1);
    const lostIn = (c: number, i: number) =>
      resultOf(c, i) === (games[i]!.home === team.teamId ? 1 : 0);
    const wonOwn = (c: number) => wonIn(c, own);
    const lostOwn = (c: number) => lostIn(c, own);
    if (owns.length === 2) {
      // A WEEK OF TWO GAMES (Q6): both won, one each way, both lost, as a
      // median week's game and median are.
      const [a, b] = owns as [number, number];
      const two = all.filter((c) => wonIn(c, a) && wonIn(c, b));
      const one = all.filter((c) => (wonIn(c, a) && lostIn(c, b)) || (lostIn(c, a) && wonIn(c, b)));
      const none = all.filter((c) => lostIn(c, a) && lostIn(c, b));
      say(
        {
          played: true,
          win: outcomeOf(two),
          split: outcomeOf(one),
          loss: outcomeOf(none),
          tiebreak,
          twoGames: true,
        },
        [two, one, none],
      );
      return;
    }
    if (median === null) {
      const wins = all.filter(wonOwn);
      const losses = all.filter(lostOwn);
      say({ played: true, win: outcomeOf(wins), loss: outcomeOf(losses), tiebreak }, [
        wins,
        losses,
      ]);
      return;
    }
    const two = all.filter((c) => wonOwn(c) && placeOf(c) === ABOVE);
    const one = all.filter(
      (c) => (wonOwn(c) && placeOf(c) === BELOW) || (lostOwn(c) && placeOf(c) === ABOVE),
    );
    const none = all.filter((c) => lostOwn(c) && placeOf(c) === BELOW);
    say(
      {
        played: true,
        win: outcomeOf(two),
        split: outcomeOf(one),
        loss: outcomeOf(none),
        tiebreak,
      },
      [two, one, none],
    );
  });
  return found;
}

/** "by A", "by A or by B", "by A, by B or by C". */
function byAny(names: readonly string[]): string {
  const bys = names.map((n) => `by ${n}`);
  return bys.length === 1 ? bys[0]! : `${bys.slice(0, -1).join(', ')} or ${bys.at(-1)!}`;
}

/**
 * The race in words (PLAN-2026-10-19 §2): its own result's routes, then a
 * word on the tiebreakers where a result can leave it level at the line.
 * Every route is one that is enough, never the only one. Null for a note
 * with nothing to say.
 */
export function raceSentence(note: RaceNote, nameOf: (teamId: string) => string): string | null {
  const parts: string[] = [];
  if (note.played && note.split !== undefined) parts.push(...medianWeekParts(note, nameOf));
  else if (note.played) {
    const w = note.win;
    if (w.kind === 'in') parts.push('In with a win.');
    else if (w.kind === 'any-loses')
      parts.push(`In with a win and a loss ${byAny(w.teams.map(nameOf))}.`);
    else if (w.kind === 'both-lose')
      parts.push(`In with a win and losses by ${nameOf(w.teams[0])} and ${nameOf(w.teams[1])}.`);
    else if (w.kind === 'help') parts.push('A win and some help get it in.');
    else if (w.kind === 'level') parts.push('A win can bring it level at the line.');
    const l = note.loss;
    if (l.kind === 'out') parts.push('Out with a loss.');
    else if (l.kind === 'any-loses')
      parts.push(`Even with a loss, a loss ${byAny(l.teams.map(nameOf))} puts it in.`);
    else if (l.kind === 'both-lose')
      parts.push(
        `Even with a loss, losses by ${nameOf(l.teams[0])} and ${nameOf(l.teams[1])} put it in.`,
      );
    else if (l.kind === 'help') parts.push('With a loss, it needs help.');
    else if (l.kind === 'level') parts.push('With a loss, it can still finish level at the line.');
  } else {
    const b = note.bye;
    if (b.kind === 'any-loses') parts.push(`A loss ${byAny(b.teams.map(nameOf))} puts it in.`);
    else if (b.kind === 'both-lose')
      parts.push(`Losses by ${nameOf(b.teams[0])} and ${nameOf(b.teams[1])} put it in.`);
    else if (b.kind === 'help') parts.push('With its week off, it needs help.');
    else if (b.kind === 'level') parts.push('With its week off, it can finish level at the line.');
  }
  if (parts.length === 0) return null;
  if (note.tiebreak || parts.some((p) => p.includes('level at the line')))
    parts.push('A tie on record at the line goes to the tiebreakers.');
  return parts.join(' ');
}

/**
 * A median week in words (PLAN-2026-10-19 §6): two wins, one, none. One win
 * enough is said once; one win not enough, then two losses, unless any loss
 * is out.
 */
function medianWeekParts(
  note: Extract<RaceNote, { played: true }>,
  nameOf: (teamId: string) => string,
): string[] {
  const parts: string[] = [];
  const s = note.split!;
  if (s.kind === 'in')
    parts.push(
      note.twoGames === true
        ? 'In with a win in either game.'
        : 'In with a win in its game or against the median.',
    );
  else {
    const w = note.win;
    if (w.kind === 'in') parts.push('In with two wins.');
    else if (w.kind === 'any-loses')
      parts.push(`In with two wins and a loss ${byAny(w.teams.map(nameOf))}.`);
    else if (w.kind === 'both-lose')
      parts.push(`In with two wins and losses by ${nameOf(w.teams[0])} and ${nameOf(w.teams[1])}.`);
    else if (w.kind === 'help') parts.push('Two wins and some help get it in.');
    else if (w.kind === 'level') parts.push('Two wins can bring it level at the line.');
    if (s.kind === 'out') return [...parts, 'Out with any loss.'];
    if (s.kind === 'any-loses')
      parts.push(`With one win, a loss ${byAny(s.teams.map(nameOf))} puts it in.`);
    else if (s.kind === 'both-lose')
      parts.push(
        `With one win, losses by ${nameOf(s.teams[0])} and ${nameOf(s.teams[1])} put it in.`,
      );
    else if (s.kind === 'help') parts.push('With one win, it needs help.');
    else if (s.kind === 'level') parts.push('With one win, it can still finish level at the line.');
  }
  const l = note.loss;
  if (l.kind === 'out') parts.push('Out with two losses.');
  else if (l.kind === 'any-loses')
    parts.push(`Even with two losses, a loss ${byAny(l.teams.map(nameOf))} puts it in.`);
  else if (l.kind === 'both-lose')
    parts.push(
      `Even with two losses, losses by ${nameOf(l.teams[0])} and ${nameOf(l.teams[1])} put it in.`,
    );
  else if (l.kind === 'help') parts.push('With two losses, it needs help.');
  else if (l.kind === 'level')
    parts.push('With two losses, it can still finish level at the line.');
  return parts;
}
